import { authApi, type AuthUser } from "@/api/auth";
import { userApi } from "@/api/users";
import { OrganizationId, OrganizationPermission } from "@/api/organizations";
import { type ProjectId, ProjectPermission } from "@/api/projects";
import type { UserId } from "@/api/users";
import { defineStore } from "pinia";
import { useQueryCache } from "@pinia/colada";
import { USER_KEYS } from "@/queries/users";
import { useAboutStore } from "./about";
import { useAuditLogsStore } from "./auditLogs";
import { useIconsStore } from "./icons";
import { useOrganizationsStore } from "./organizations";
import { useProjectsStore } from "./projects";
import { useSubscriptionsStore } from "./subscriptions";
import { useTeamsStore } from "./teams";
import { useTicketListsStore } from "./ticketLists";
import { useUsersStore } from "./users";
import { useWorkLogsStore } from "./workLogs";
import { useAlertsStore } from "./alerts";
import * as Sentry from "@sentry/vue";

export type AuthState = {
    isAuthenticated: boolean;
    route: string;
    user: AuthUser | null;
};

export const useAuthStore = defineStore("auth", {
    state: (): AuthState => ({
        isAuthenticated: false,
        route: "/",
        user: null,
    }),
    getters: {
        hasPermission(state) {
            return (project: ProjectId, perm: ProjectPermission) =>
                state.user?.isAdmin ||
                !!state.user?.projectPermissions
                    .get(project)
                    ?.some((p) => p === perm || p === ProjectPermission.ProjectAdministrator);
        },
        hasOrganizationPermission(state) {
            return (organization: OrganizationId, perm: OrganizationPermission) =>
                state.user?.isAdmin ||
                !!state.user?.organizationPermissions
                    .get(organization)
                    ?.some(
                        (p) => p === perm || p === OrganizationPermission.OrganizationAdministrator
                    );
        },
    },
    actions: {
        async authenticated() {
            const auth = await authApi.authenticated();
            if (auth !== null) {
                this.$patch({ user: auth, isAuthenticated: true });
                Sentry.setUser({ email: auth.email });
            }

            return auth !== null;
        },
        async login(userName: string, password: string, rememberMe: boolean) {
            const result = await authApi.login(userName, password, rememberMe);

            if (result.status === "ok") {
                await this.signedIn(result.user);
            }

            return result.status;
        },
        async loginTwoFactor(code: string, rememberMe: boolean, rememberClient: boolean) {
            const auth = await authApi.loginTwoFactor(code, rememberMe, rememberClient);
            if (auth) await this.signedIn(auth);
            return auth !== null;
        },
        async loginRecoveryCode(code: string, rememberMe: boolean) {
            const auth = await authApi.loginRecoveryCode(code, rememberMe);
            if (auth) await this.signedIn(auth);
            return auth !== null;
        },
        async signedIn(auth: AuthUser) {
            const subStore = useSubscriptionsStore();
            this.$patch({ user: auth, isAuthenticated: true });
            await subStore.createConnection();
            Sentry.setUser({ email: auth.email });
        },
        async externalProviders() {
            return await authApi.externalProviders();
        },
        async externalLinks() {
            return await authApi.externalLinks();
        },
        async unlinkExternal(scheme: string) {
            await authApi.unlinkExternal(scheme);
        },
        async twoFactorStatus() {
            return await authApi.twoFactorStatus();
        },
        async setupTwoFactor() {
            return await authApi.setupTwoFactor();
        },
        async enableTwoFactor(code: string) {
            return await authApi.enableTwoFactor(code);
        },
        async disableTwoFactor(code: string) {
            await authApi.disableTwoFactor(code);
        },
        async regenerateRecoveryCodes(code: string) {
            return await authApi.regenerateRecoveryCodes(code);
        },
        async confirm(userId: UserId, token: string, userName: string, password: string) {
            const auth = await authApi.confirm(userId, token, userName, password);
            this.$patch({ user: auth, isAuthenticated: true });
        },
        async logout() {
            await authApi.logout();
            Sentry.setUser(null);

            this.$state = {
                isAuthenticated: false,
                route: "/",
                user: null,
            };

            this.resetIdentityStores();
        },
        async impersonate(userId: UserId) {
            await authApi.impersonate(userId);
            await this.switchIdentity();
        },
        async exitImpersonation() {
            await authApi.exitImpersonation();
            await this.switchIdentity();
        },
        // Entering or leaving impersonation swaps who the session is, so every store holding
        // identity-scoped data is cleared and the realtime connection is re-established under the
        // new user, mirroring the login/logout flows.
        async switchIdentity() {
            const subscriptionsStore = useSubscriptionsStore();
            await subscriptionsStore.closeConnection();
            this.resetIdentityStores();
            await this.authenticated();
            await subscriptionsStore.createConnection();
        },
        resetIdentityStores() {
            // The query cache holds identity-scoped server data too.
            const queryCache = useQueryCache();
            for (const entry of queryCache.getEntries()) {
                queryCache.remove(entry);
            }

            const aboutStore = useAboutStore();
            const alertsStore = useAlertsStore();
            const auditLogsStore = useAuditLogsStore();
            const iconsStore = useIconsStore();
            const organizationsStore = useOrganizationsStore();
            const projectsStore = useProjectsStore();
            const subscriptionsStore = useSubscriptionsStore();
            const teamsStore = useTeamsStore();
            const ticketListsStore = useTicketListsStore();
            const usersStore = useUsersStore();
            const workLogsStore = useWorkLogsStore();

            aboutStore.$reset();
            alertsStore.$reset();
            auditLogsStore.$reset();
            iconsStore.$reset();
            organizationsStore.$reset();
            projectsStore.$reset();
            subscriptionsStore.$reset();
            teamsStore.$reset();
            ticketListsStore.$reset();
            usersStore.$reset();
            workLogsStore.$reset();
        },
        async uploadAvatar(file: File) {
            if (!this.user) {
                throw new Error("Cannot set avatar without a user.");
            }

            const avatar = await userApi.uploadUserAvatar(file);

            this.user.avatarId = avatar.id;
            await this.invalidateAvatarQueries();
        },
        async deleteAvatar() {
            if (!this.user?.avatarId) {
                throw new Error("Cannot set avatar without a user.");
            }

            await userApi.deleteUserAvatar();

            this.user.avatarId = null;
            await this.invalidateAvatarQueries();
        },
        // Other components render this user through the cached user list, so
        // both it and the avatar set must refresh.
        async invalidateAvatarQueries() {
            const queryCache = useQueryCache();
            await queryCache.invalidateQueries({ key: USER_KEYS.list() });
            await queryCache.invalidateQueries({ key: USER_KEYS.avatars() });
        },
    },
});
